import { Autocomplete, TextField, Box, Chip } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useTranslation } from 'react-i18next';

/**
 * Autocomplete for selecting units, with custom rendering for tags and sensors.
 * @param {Object} props
 * @param {Array} props.units - List of unit objects
 * @param {Array} props.value - Selected units
 * @param {Function} props.onChange - Callback when selection changes
 */
const UnitAutocomplete = ({ units, value, onChange }) => {
  const { t } = useTranslation('view-data');
  return (
    <Autocomplete
      id="unit-autocomplete"
      sx={{ width: '100%' }}
      multiple
      clearOnBlur
      options={units}
      getOptionLabel={(unit) => `${unit.Name} (${unit.UnitId})` || ''}
      filterOptions={(options, { inputValue }) => {
        const search = inputValue.trim().toLowerCase();
        return options.filter((unit) => {
          const nameMatch = unit.Name?.toLowerCase().includes(search);
          const tagMatch = unit.Tags?.some((tag) =>
            tag.Text?.toLowerCase().includes(search),
          );
          const sensorMatch = unit.UnitSensors?.some((s) =>
            String(s.Sensor?.SensorId).includes(search),
          );
          const unitIdMatch = String(unit.UnitId).includes(search);
          return nameMatch || tagMatch || sensorMatch || unitIdMatch;
        });
      }}
      value={value || []}
      onChange={(event, newValue) => onChange(newValue)}
      renderOption={(props, unit) => {
        const tagTexts = unit.Tags
          ? Array.from(new Set(unit.Tags.map((t) => t.Text)))
          : [];
        const sensorIds = unit.UnitSensors
          ? Array.from(
              new Set(
                unit.UnitSensors.map((s) => s.Sensor?.SensorId).filter(Boolean),
              ),
            )
          : [];
        return (
          <li {...props} key={props.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label={`${unit.Name} (${unit.UnitId})`} />
              {tagTexts.map((tag) => (
                <Chip key={tag} label={tag} color="info" size="small" />
              ))}
              {sensorIds.map((sid) => (
                <Chip
                  key={sid}
                  icon={<SensorsIcon fontSize="small" />}
                  label={sid}
                  color="success"
                  size="small"
                />
              ))}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={t('units')} placeholder={t('search')} />
      )}
    />
  );
};

export default UnitAutocomplete;
